import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import React, { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import NavBarButton from '../NavBarButton/NavBarButton'
import ContactContext from 'contexts/contactContext'
import useToggle from 'hooks/useToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import NavBarContactsItems from './NavBarContactsItems'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import Modal from 'LuxKit/Modal/Modal'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import useOrders from 'hooks/Order/useOrders'
import { useAppSelector } from 'hooks/reduxHooks'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'

interface Props {
  className?: string
  /**
   * The minimum breakpoint when the text disappears
   */
  textBreakpoint?: App.ScreenSize
}

function NavBarContactsButton(props: Props) {
  const { className, textBreakpoint } = props
  const [open, toggleOpen, _, closeDropdown] = useToggle()
  const intl = useIntl()

  const { currentRegionCode } = useContext(GeoContext)
  const chatEnabled = useAppSelector(isSupportAssistantEnabled)
  const [upcomingOrders] = useOrders('upcoming')
  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const contact = useContext(ContactContext)

  const modalTitle = intl.formatMessage({
    id: 'LxFxnC/py2',
    defaultMessage: 'Contact us 24/7',
    description: 'Contact us modal title',
  })

  const onNavClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    if (isMobile) {
      showModal(resolve => <Modal title={modalTitle}>
        <NavBarContactsItems onItemClick={resolve} />
      </Modal>)
    } else {
      toggleOpen()
    }
  }, [isMobile, showModal, toggleOpen, modalTitle])

  const has247Support = !NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)

  if (upcomingOrders.length && chatEnabled) {
    // Push the user to use our chat if they have an upcoming order instead of calling us
    return null
  }

  const contactUsText = intl.formatMessage({
    id: 'R24eysa25z',
    defaultMessage: 'Contact us',
    description: 'Contact us button text for tablet and below',
  })

  const contactUs7DaysText = intl.formatMessage({
    id: 'M27wp7bE6g',
    defaultMessage: 'Contact us 7 days',
    description: 'Contact us text for regions without 24/7 support',
  })

  const contactUs247Text = intl.formatMessage({
    id: 'icDvyB6I8u',
    defaultMessage: 'Contact us 24 hours, 7 days',
    description: 'Contact us text for regions with 24/7 support',
  })

  const callText = intl.formatMessage({
    id: 'k6ZGlXEQRr',
    defaultMessage: 'Call',
    description: 'Call button text for mobile and tablet',
  })

  return <>
    <Group direction="horizontal" gap={4} verticalAlign="center" className={className}>
      <CSSBreakpoint min={textBreakpoint}>
        <CSSBreakpoint max="tablet">
          <BodyText variant="medium" wrap="no-wrap">{contactUsText}</BodyText>
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <Caption variant="medium" wrap="no-wrap">
            {!has247Support && contactUs7DaysText}
            {has247Support && contactUs247Text}
          </Caption>
        </CSSBreakpoint>
      </CSSBreakpoint>
      <NavBarButton
        size="small"
        icon={<LinePhoneIcon />}
        dropdown
        ref={buttonRef}
        onClick={onNavClick}
      >
        <CSSBreakpoint as="span" max="tablet">{callText}</CSSBreakpoint>
        <CSSBreakpoint as="span" min="desktop">{contact.defaultContact.humanReadable}</CSSBreakpoint>
      </NavBarButton>
    </Group>
    <DropdownList
      size="S"
      open={!isMobile && open}
      onClose={closeDropdown}
      anchorRef={buttonRef}
      triggerRef={buttonRef}
    >
      <NavBarContactsItems />
    </DropdownList>
  </>
}

export default React.memo(NavBarContactsButton)
